import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";
import { jwtDecode } from "jwt-decode";
import axios from "axios";
import followApi from "../apis/follow.api";
import { useStore } from "./store";
import { emitAuthEvent } from "./authSync";

export const createRoleStore = (roleKey) =>
  create(
    persist(
      (set, get) => ({
        /* ----------------------------------
         * Lifecycle guards
         * ---------------------------------- */
        isHydrating: true,
        hasVerified: false,
        isVerifying: false,
        refreshTimeoutId: null,
        hasListener: false,

        finishHydration: () => set({ isHydrating: false }),

        user: {
          isAuthenticated: false,
          data: null,
          role: roleKey,
        },
        /* ----------------------------------
         * Verify session (ONCE / FORCEABLE)
         * ---------------------------------- */

        verifySession: async ({ force = false, emit = false } = {}) => {
          const { hasVerified, isVerifying } = get();

          if (!force && (hasVerified || isVerifying)) return;

          set({
            isVerifying: true,
            hasVerified: force ? false : hasVerified,
          });

          const verifyMap = {
            admin: `${process.env.REACT_APP_BACKEND}/admin/verify-session`,
            organizer: `${process.env.REACT_APP_BACKEND}/auth/org/verify`,
            attendee: `${process.env.REACT_APP_BACKEND}/auth/verify-session`,
            scanner: `${process.env.REACT_APP_BACKEND}/scanner/verify-session`,
          };

          try {
            const res = await axios.post(
              verifyMap[roleKey],
              {},
              { withCredentials: true }
            );

            const data = res?.data?.data;

            set({
              user: {
                isAuthenticated: true,
                data,
                role: data?.roleType ?? roleKey,
              },
              hasVerified: true,
              isVerifying: false,
              isHydrating: false,
            });

            // if (emit) {
            //   emitAuthEvent("login", roleKey);
            // }

            if (data?.roleType === "attendee") {
              followApi.listFollowers({
                success: (r) =>
                  useStore.getState().setFollowers(r?.data?.data ?? []),
                error: () => useStore.getState().setFollowers([]),
              });
            }

            get().scheduleRefresh();
          } catch {
            set({
              user: { isAuthenticated: false, data: null, role: roleKey },
              hasVerified: true,
              isVerifying: false,
              isHydrating: false,
            });
          }
        },

        /* ----------------------------------
         * Silent refresh (single timer)
         * ---------------------------------- */
        scheduleRefresh: () => {
          const { refreshTimeoutId } = get();
          if (refreshTimeoutId) return;

          try {
            const token = document.cookie
              .split("; ")
              .find((r) => r.startsWith("signature="))
              ?.split("=")[1];

            if (!token) return;

            const { exp } = jwtDecode(token);
            if (!exp) return;

            const refreshIn = exp * 1000 - Date.now() - 2 * 60 * 1000;

            if (refreshIn <= 0) return;

            const id = setTimeout(() => {
              set({ refreshTimeoutId: null });
              get().refreshSession();
            }, refreshIn);

            set({ refreshTimeoutId: id });
          } catch {}
        },

        refreshSession: async () => {
          const refreshMap = {
            admin: `${process.env.REACT_APP_BACKEND}/auth/admin/refresh`,
            organizer: `${process.env.REACT_APP_BACKEND}/auth/org/refresh`,
            attendee: `${process.env.REACT_APP_BACKEND}/auth/refresh`,
            scanner: `${process.env.REACT_APP_BACKEND}/scanner/refresh`,
          };

          try {
            const res = await fetch(refreshMap[roleKey], {
              method: "POST",
              credentials: "include",
            });

            if (res.ok) {
              set({ hasVerified: false });
              await get().verifySession();
            } else {
              get().logout();
            }
          } catch {
            get().logout();
          }
        },

        /* ----------------------------------
         * Logout (sync-aware)
         * ---------------------------------- */
        logout: ({ silent = false } = {}) => {
          const { refreshTimeoutId } = get();
          if (refreshTimeoutId) clearTimeout(refreshTimeoutId);

          set({
            user: { isAuthenticated: false, data: null, role: roleKey },
            hasVerified: true,
            refreshTimeoutId: null,
          });

          // if (!silent) {
          //   emitAuthEvent("logout", roleKey);
          // }
        },
      }),
      {
        name: `${roleKey}-auth`,
        storage: createJSONStorage(() => sessionStorage),
        onRehydrateStorage: () => (state) => {
          state?.finishHydration?.();
        },
      }
    )
  );
